import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { DateRange } from '@/components/DataTable/FilterControls/Filters/DateRangeFilter'
import useCustomerErpFilter from '@/hooks/filters/credit/useCustomerErpFilter'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  creditLimitReviewsSearchSchema,
  useCreditLimitReviews,
} from '@/hooks/queries/credit/useCreditLimitReviews'
import { useTablePagination } from '@/hooks/useTablePagination'
import { kindLabelKey } from '@/lib/utils/clrKind'
import { DateRangeControl, MultipleToggleListControl } from '@/lib/utils/filters'
import { CLR_KINDS, CLR_STATUSES, type ClrKind, type ClrStatus } from '@repo/constants'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreditLimitReviewsDataTable } from './-components/CreditLimitReviewsDataTable'

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/')({
  component: RouteComponent,
  validateSearch: (search) => creditLimitReviewsSearchSchema.parse(search),
})

const SCOPES = ['all', 'mine'] as const
type Scope = (typeof SCOPES)[number]

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const { canUser } = usePermissions()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const { erpFilter, erp, setErp } = useCustomerErpFilter(search.erp)

  // Page-level scope, applied immediately (not part of the filter panel): every review, or only
  // the ones the viewer took part in.
  const scope: Scope = search.involvement ?? 'all'
  const onScopeChange = (value: Scope) => {
    navigate({
      search: { ...search, involvement: value === 'all' ? undefined : value, page: 1 },
    })
  }

  const [kinds, setKinds] = useState<ClrKind[]>(search.kind ?? [])
  const kindFilter = {
    control: MultipleToggleListControl,
    name: 'kind',
    title: t('clr.filters.kind'),
    // Alphabetical by translated label, so the order adapts per language — same as the Periodic
    // Reviews page, which filters the same eight triggers.
    options: CLR_KINDS.map((kind) => ({
      value: kind,
      label: t(kindLabelKey(kind)),
    })).sort((a, b) => a.label.localeCompare(b.label)),
    value: kinds,
    onChange: setKinds,
  }

  const [statuses, setStatuses] = useState<ClrStatus[]>(search.status ?? [])
  const statusFilter = {
    control: MultipleToggleListControl,
    name: 'status',
    title: t('clr.filters.status'),
    options: CLR_STATUSES.map((status) => ({
      value: status,
      label: t(`clr.filterStatus.${status}`),
    })),
    value: statuses,
    onChange: setStatuses,
  }

  const [createdRange, setCreatedRange] = useState<DateRange>({
    from: search.createdFrom,
    to: search.createdTo,
  })
  const createdAtFilter = {
    control: DateRangeControl,
    name: 'createdAt',
    title: t('clr.filters.createdAt'),
    options: [],
    value: createdRange,
    onChange: setCreatedRange,
    fromParamName: 'createdFrom',
    toParamName: 'createdTo',
  }

  const filters = [kindFilter, statusFilter, erpFilter, createdAtFilter]

  const reviewsQuery = useCreditLimitReviews({ ...search, pageSize })

  const showResults = () => {
    navigate({
      search: {
        ...search,
        keyword: keyword || undefined,
        erp: erp?.length ? erp : undefined,
        kind: kinds?.length ? kinds : undefined,
        status: statuses?.length ? statuses : undefined,
        createdFrom: createdRange.from,
        createdTo: createdRange.to,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setErp([])
    setKinds([])
    setStatuses([])
    setCreatedRange({ from: undefined, to: undefined })

    navigate({
      search: {
        ...search,
        keyword: undefined,
        erp: undefined,
        kind: undefined,
        status: undefined,
        createdFrom: undefined,
        createdTo: undefined,
        page: 1,
      },
    })
  }

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <PageHeader>{t('clr.pageHeader')}</PageHeader>
        <HStack gap="sm" align="center">
          {canUser('credit_review_events_view') && (
            <Button type="button" variant="secondary" asChild>
              <Link to="/credit/credit-limit-reviews/review-events">
                {t('clr.viewReviewEvents')}
              </Link>
            </Button>
          )}
          <Button type="button" variant="secondary" asChild>
            <Link to="/credit/credit-limit-reviews/pending-confirmations">
              {t('clr.pendingConfirmations.button')}
            </Link>
          </Button>
          <Select value={scope} onValueChange={(value) => onScopeChange(value as Scope)}>
            <SelectTrigger className="bg-background w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCOPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`clr.scope.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <CreditLimitReviewsDataTable
            filters={filters as Filter<unknown>[]}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('clr.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            reviewsQuery={reviewsQuery}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
